import cn from 'classnames'
import { memo, useCallback, useState } from 'react'

import { useFormField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { Input } from 'core-components/library/inputs'
import { Tooltip } from 'core-components/library/tooltip'
import { Link } from 'shared/components/link/Link'

import { ReadOnlyLinkList } from './components/readOnlyLinkList/ReadOnlyLinkList'
import { ILinkListProps, TLinkListItem } from './models'
import { linkListTranslation } from './translation'

import styles from './LinkList.module.scss'

export const LinkList = memo<ILinkListProps>(
  ({ label, field, formKey, defaultValue = [], isDisabled, isReadOnly = true }) => {
    /** ----------------------- Variables ------------------------ */

    const [isInputVisible, setIsInputVisible] = useState(false)
    const [links, setLinks] = useState(defaultValue)
    const [link, setLink] = useState('')
    const [id, setId] = useState(links.length)
    const { handleChange } = useFormField({
      defaultValue,
      field,
      formKey,
    })

    /** ----------------------- Handlers ------------------------ */

    const handleAddButtonClick = useCallback(() => {
      setIsInputVisible(true)
    }, [])

    const handleSaveButtonClick = useCallback(() => {
      const newLink: TLinkListItem = {
        id,
        private: false,
        value: link,
      }

      setLinks((prevState) => {
        const newState = [...prevState, newLink]
        handleChange(newState).then()
        return newState
      })
      setLink('')
      setId((prevState) => ++prevState)
      setIsInputVisible(false)
    }, [handleChange, id, link])

    const handleCancelButtonClick = useCallback(() => {
      setLink('')
      setIsInputVisible(false)
    }, [])

    const handleDeleteLink = useCallback(
      (id: number) => {
        setLinks((prevState) => {
          const newState = prevState.filter((link) => link.id !== id)
          handleChange(newState).then()
          return newState
        })
      },
      [handleChange]
    )

    const handleSelectIsPrivate = useCallback(
      (id: number, value: boolean) => {
        setLinks((prevState) => {
          const newState = prevState.map((link) => {
            if (link.id === id) {
              return { ...link, private: value }
            }
            return link
          })
          handleChange(newState).then()
          return newState
        })
      },
      [handleChange]
    )

    /** ----------------------- RenderTips ------------------------ */

    const renderAddTip = useCallback(() => <Translation text={linkListTranslation.addLink} />, [])
    const renderSaveTip = useCallback(() => <Translation text={linkListTranslation.save} />, [])
    const renderCancelTip = useCallback(() => <Translation text={linkListTranslation.cancel} />, [])
    const renderIsPrivateTip = useCallback(
      () => <Translation text={linkListTranslation.isPrivate} />,
      []
    )

    /** ----------------------- Render ------------------------ */

    if (isReadOnly) {
      return <ReadOnlyLinkList label={label} links={links} />
    }

    return (
      <div className={styles.linkList}>
        <div className={styles.label}>
          <Translation text={label} span />
          <Tooltip tipContent={renderAddTip}>
            <Button
              disabled={isDisabled || isInputVisible}
              onClick={handleAddButtonClick}
              iconButton="kbase kbase-add"
            />
          </Tooltip>
        </div>

        {/** --------------- New link ----------------- */}

        <div className={cn(styles.inputContainer, { [styles.hidden]: !isInputVisible })}>
          <Input
            disabled={isDisabled}
            classes={{
              root: styles.input,
            }}
            label="Url"
            value={link}
            onChange={setLink}
          />
          <Tooltip tipContent={renderSaveTip}>
            <Button
              onClick={handleSaveButtonClick}
              color="success"
              iconButton="kbase kbase-save"
              disabled={!link}
            />
          </Tooltip>
          <Tooltip tipContent={renderCancelTip}>
            <Button
              onClick={handleCancelButtonClick}
              color="danger"
              iconButton="kbase kbase-close"
            />
          </Tooltip>
        </div>

        {/** --------------- Link list ----------------- */}

        <div className={styles.list}>
          {links.map((link) => (
            <div key={link.id} className={styles.linkContainer}>
              <Link
                isDisabled={isDisabled}
                link={link.value}
                text={link.value}
                onDelete={() => handleDeleteLink(link.id)}
                className={styles.link}
              />
              <Tooltip className={styles.isPrivate} tipContent={renderIsPrivateTip}>
                <Checkbox
                  disabled={isDisabled}
                  className={styles.isPrivate}
                  label="Private"
                  checked={link.private}
                  onChange={(value) => handleSelectIsPrivate(link.id, value)}
                />
              </Tooltip>
            </div>
          ))}
        </div>
      </div>
    )
  }
)

LinkList.displayName = 'LinkList'
